import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

/**
 * Comparte la URL actual.
 *
 * No construye nada: como la URL ya refleja exactamente lo que se está mirando
 * (equipo, pestaña, solución y documento), alcanza con la ubicación actual. El
 * path lo da el router y solo el host sale de window, así que también funciona
 * si algún día la app se sirve bajo un basename.
 *
 * En mobile usa el menú nativo de compartir, que es el que ofrece WhatsApp. En
 * desktop copia al portapapeles, y si eso falla (contexto no seguro, permisos)
 * cae a abrir WhatsApp Web con el link.
 */
export default function ShareButton({ title, label = 'Compartir' }) {
    const location = useLocation();
    const [copied, setCopied] = useState(false);

    const handleShare = async () => {
        const url = `${window.location.origin}${location.pathname}${location.search}`;

        if (navigator.share) {
            try {
                await navigator.share({ title, url });
                return;
            } catch (error) {
                if (error?.name === 'AbortError') return; // el usuario canceló
                console.warn('No se pudo usar el menú de compartir:', error);
            }
        }

        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (error) {
            console.warn('No se pudo copiar al portapapeles:', error);
            window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, '_blank', 'noreferrer');
        }
    };

    return (
        <StyledShareButton
            type="button"
            className="btn btn-sm"
            onClick={handleShare}
            title={label}
        >
            {copied ? (
                <><i className="fa-solid fa-check"></i> ¡Link copiado!</>
            ) : (
                <><i className="fa-solid fa-share-nodes"></i> {label}</>
            )}
        </StyledShareButton>
    );
}

// Verde suave tipo WhatsApp: fondo muy claro, y un verde más oscuro para el
// borde, el icono y el texto. El && duplica la especificidad de la clase para
// ganarle a los colores de .btn de Bootstrap sin recurrir a !important.
const VERDE_FONDO = '#E9F9EF';
const VERDE_FONDO_HOVER = '#D8F3E2';
const VERDE_BORDE = '#0E7A47';

const StyledShareButton = styled.button`
    && {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        white-space: nowrap;

        background-color: ${VERDE_FONDO};
        border: 1px solid ${VERDE_BORDE};
        color: ${VERDE_BORDE};
        font-weight: 600;

        transition: background-color 0.15s ease-in-out;
    }

    && i {
        color: ${VERDE_BORDE};
    }

    &&:hover {
        background-color: ${VERDE_FONDO_HOVER};
        border-color: ${VERDE_BORDE};
        color: ${VERDE_BORDE};
    }

    &&:focus-visible {
        outline: 2px solid ${VERDE_BORDE};
        outline-offset: 2px;
        box-shadow: none;
    }
`
